import {Component, Emit, Prop, Watch, Vue} from 'vue-property-decorator';
import moment from 'moment';
import { HarvestRecordsResponse, HarvestRecordResponse } from "@/types/APIResponse";
import { HarvestRecords, HarvestRecordItem, Crop, ShippingRecordHarvestRecord } from "@/types/Component";
import Modal from "@/assets/common/Modal/Modal.vue";
import ModalBase from "@/assets/common/Modal/Modal.vue";
import { toCamel } from 'snake-camel';

type DisplayItem = {
    harvestRecordId: string;
    harvestDate: string;
    measurementTimingId: string;
    cropId: string;
    cropName: string;
    cropPhonetic: string;
    plantingId: string;
    plantingName: string;
    agriFieldId: string;
    agriFieldName: string;
    workStartDate: string | null;
    workEndDate: string | null;
}

type HarvestRecordsList = {
    harvestRecords: HarvestRecords[]
}

type SelectedPlanting = {
    plantingId: string;
    agriFieldId: string;
}

@Component({
    components: { Modal }
})
export default class SelectFromHarvestRecord extends ModalBase {
    @Prop({
        default: () => "収穫記録から選択"
    })
    title!: string

    @Prop({ default: () => {
            return {
                harvestRecordId: [],
                agriFieldId: null,
                plantingId: null
            }
        }
    })
    private preSelectItem!: {
        harvestRecordId: string[];
        agriFieldId: string | null;
        plantingId: string | null;
    }

    @Prop({ default: ''})
    private recordDate!: string

    private harvestRecords: HarvestRecords[] = []
    private harvestRecordDetail: HarvestRecordItem | null = null
    private apiBaseUrl = '/harvest_records'

    private allItems: DisplayItem[] = []
    private cropList: Crop[] = []
    private plantingList: DisplayItem[] = []
    private harvestDateList: DisplayItem[] = []
    private selectedCrop = ''
    private selectedPlantingKey = ''
    private selectedPlanting: SelectedPlanting | null = null
    private selectedHarvestRecords: DisplayItem[] = []
    private showHarvestRecordDetail = false
    private showHarvestRecordDetailId = ''
    private isAllTerm = false
    private sortByPlantingFlag = 1
    private sortByTermFlag = 1
    private sortByHarvestDateFlag = 1
    private sortByHarvestDateIconFlag = 1

    async created() {
        await this.getHarvestRecords()
        this.init()
    }

    private init() {
        this.createCropList(this.allItems)
        this.search()
    }
    
    private async getHarvestRecords() {
        return this.$apiClient.doRequest('get', this.apiBaseUrl)
        .then((res: HarvestRecordsResponse) => {
            this.selectedHarvestRecords = []
            this.harvestRecords = (toCamel(res) as HarvestRecordsList).harvestRecords
            this.allItems = this.harvestRecords.map(record => {
                const displayItem = {
                    harvestRecordId: record.id,
                    harvestDate: record.harvestDate,
                    measurementTimingId :record.measurementTimingId,
                    cropId: record.planting.crop.id,
                    cropName: record.planting.crop.name,
                    cropPhonetic: record.planting.crop.phonetic,
                    plantingId: record.planting.id,
                    plantingName: record.planting.name,
                    workStartDate: record.planting.workStartDate,
                    workEndDate: record.planting.workEndDate,
                    agriFieldId: record.agriField.id,
                    agriFieldName: record.agriField.name
                }
                // 初期選択
                if(this.preSelectItem.harvestRecordId.indexOf(displayItem.harvestRecordId) > -1) {
                    // 事前に収穫記録選択済みの場合、作目、作付（圃場）、収穫日を選択状態にする
                    this.selectedHarvestRecords.push(displayItem)
                    this.selectedCrop = displayItem.cropId
                    this.selectedPlantingKey = displayItem.plantingId + displayItem.agriFieldId
                    this.selectedPlanting = {
                        plantingId: displayItem.plantingId,
                        agriFieldId: displayItem.agriFieldId
                    }
                    // 作付が期間外のときは「期間外も表示」を有効にする
                    this.isAllTerm = !this.isWithinTheTerm(displayItem.workStartDate, displayItem.workEndDate)
                } else if(this.preSelectItem.plantingId === displayItem.plantingId && this.preSelectItem.agriFieldId === displayItem.agriFieldId) {
                    // 事前に作付と圃場が選択済みの場合、作目、作付（圃場）を選択状態にする
                    this.selectedCrop = displayItem.cropId
                    this.selectedPlantingKey = displayItem.plantingId + displayItem.agriFieldId
                    this.selectedPlanting = {
                        plantingId: displayItem.plantingId,
                        agriFieldId: displayItem.agriFieldId
                    }
                    // 作付が期間外のときは「期間外も表示」を有効にする
                    this.isAllTerm = !this.isWithinTheTerm(displayItem.workStartDate, displayItem.workEndDate)
                }
                return displayItem
            })
        })
        .catch(() => {
          this.$toasted.error('データの取得に失敗しました。')
        })
    }
    
    private selectCrop(cropId: string) {
        this.selectedCrop = cropId
        this.selectedPlanting = null
        this.selectedHarvestRecords = []
        this.search()
    }

    private selectPlanting(planting: SelectedPlanting) {
        this.harvestDateList = []
        this.selectedPlanting = {
            plantingId: planting.plantingId,
            agriFieldId: planting.agriFieldId
        }
        if (planting) {
            this.selectedPlantingKey = planting.plantingId + planting.agriFieldId
            const filteredByDate = this.filterList(this.allItems, '', null, this.recordDate)
            this.harvestDateList = this.filterList(filteredByDate, this.selectedCrop, this.selectedPlanting, this.recordDate)
            this.sortByHarvestDate()
        }
    }

    private search(): void {
        const filteredByDate = this.filterList(this.allItems, '', null, this.recordDate)
        this.cropList = this.createCropList(filteredByDate)
        if (!this.cropList.find(crop => crop.id === this.selectedCrop)) {
            this.selectedCrop = ''
            this.selectedPlanting = null
        }
        const filteredByCrops = this.filterList(filteredByDate, this.selectedCrop, null, this.recordDate)
        this.plantingList = this.createPlantingList(filteredByCrops)
        if (!this.plantingList.find(planting => planting.plantingId === this.selectedPlanting?.plantingId && planting.agriFieldId === this.selectedPlanting.agriFieldId)){
            this.selectedPlanting = null
        }
        this.harvestDateList = this.filterList(filteredByDate, this.selectedCrop, this.selectedPlanting, this.recordDate)
        this.sortByHarvestDate()
    }

    private filterList(beforeArray: DisplayItem[], cropId: string, planting: SelectedPlanting | null, recordDate: string): DisplayItem[] {
        let filteredItems: Array<DisplayItem> = []
        filteredItems = beforeArray.filter((item)=>{
            return (cropId == '' || cropId == item.cropId)
              && (this.isAllTerm || recordDate == '' || this.isWithinTheTerm(item.workStartDate, item.workEndDate))
              && (planting == null || (item.plantingId == planting.plantingId && item.agriFieldId == planting.agriFieldId))
        })
        return filteredItems
    }

    private isWithinTheTerm(start: string | null, end: string | null) {
        const recordDate = moment(this.recordDate)
        const startDate = moment(start)
        const endDate = moment(end)
        return (!startDate.isValid() || recordDate.isSameOrAfter(startDate)) && (!endDate.isValid() || recordDate.isSameOrBefore(endDate))
    }

    private createCropList(array: DisplayItem[]): Crop[] {
        let cropList = []
        const cropAll: Crop = {
            id: '',
            name: 'すべて',
            phonetic: ''
        }

        const cropIdList = array.map((item)=>item.cropId)
        cropList = array.filter((item, index)=>{
            return cropIdList.indexOf(item.cropId) === index
        })
        .map((item)=>{
            const crop = {
                id: item.cropId,
                name: item.cropName,
                phonetic: item.cropPhonetic
            }
            return crop
        })
        cropList = cropList.sort((a: Crop, b: Crop)=>this.compare(a.phonetic, b.phonetic))
        cropList.unshift(cropAll)
        return cropList
    }

    private createPlantingList(array: Array<DisplayItem>): Array<DisplayItem> {
        this.plantingList = []
        const plantingKeyList = array.map((item) => item.plantingId + item.agriFieldId)
        const plantingList = array.filter((item, index)=>{
            const key = item.plantingId + item.agriFieldId
            return plantingKeyList.indexOf(key) === index
        })


        return plantingList.sort((a: DisplayItem, b: DisplayItem)=>this.compare(a.plantingName, b.plantingName))
    }

    private sortByPlanting() {
        this.plantingList.sort((a, b) => a.plantingName > b.plantingName ? this.sortByPlantingFlag : -this.sortByPlantingFlag)
        this.sortByPlantingFlag = -this.sortByPlantingFlag
    }

    private sortByTerm() {
        this.plantingList.sort((a, b) =>
            moment(a.workStartDate).isValid() && moment(b.workStartDate).isValid()
                ? moment(a.workStartDate).isSameOrBefore(moment(b.workStartDate))
                    ? this.sortByTermFlag
                    : -this.sortByTermFlag
                : 1
        )
        this.sortByTermFlag = -this.sortByTermFlag
    }

    // 収穫日のソート（昇順固定）
    private sortByHarvestDate() {
        this.harvestDateList.sort((a, b) =>
            moment(b.harvestDate).isSameOrAfter(moment(a.harvestDate)) ? this.sortByHarvestDateFlag : -this.sortByHarvestDateFlag
        )
    }

    private sortByHarvestDates() {
        this.harvestDateList.sort((a, b) =>
            moment(a.harvestDate).isSameOrAfter(moment(b.harvestDate)) ? this.sortByHarvestDateIconFlag : -this.sortByHarvestDateIconFlag
        )
        this.sortByHarvestDateIconFlag = -this.sortByHarvestDateIconFlag
    }

    private compare(a: string | number | null, b: string | number | null, reverse?: boolean) {
        let comparison = 0;
        if(a == null) {
            return -1
        } else if(b == null) {
            return 1
        }
        
        if (a > b) {
            comparison = 1;
        } else if (a < b) {
            comparison = -1;
        }

        if (reverse == true) {
            // 降順ソート
            comparison = comparison * -1
        }

        return comparison;
    }

    private checkSelectKey(plantingId: string, agriFieldId: string, e: Event) {
        if(this.selectedHarvestRecords.length > 0
            && (this.selectedHarvestRecords[0].plantingId != plantingId
            || this.selectedHarvestRecords[0].agriFieldId != agriFieldId)
            ) {
            if(window.confirm('作付または圃場が異なる収穫記録を複数選択することはできません。\n選択済みの収穫記録はクリアされますがよろしいですか？')) {
                this.selectedHarvestRecords = []
            } else {
                e.preventDefault()
            }
        }
    }

    private showDetail(id: string) {
        if(this.showHarvestRecordDetailId == id) {
            this.showHarvestRecordDetail = false
            this.showHarvestRecordDetailId = ''
            this.harvestRecordDetail = null
        } else {
            this.showHarvestRecordDetail = true
            this.showHarvestRecordDetailId = id
            this.getHarvestRecordDetail(id)
        }
    }

    private getHarvestRecordDetail(id: string) {
        this.$apiClient.doRequest('get', `${this.apiBaseUrl}/${id}`)
        .then((res: HarvestRecordResponse) => {
          this.harvestRecordDetail = toCamel(res) as HarvestRecordItem
        })
        .catch(() => {
          this.$toasted.error('データの取得に失敗しました。')
        })
    }

    private calculateQuantity(area: number, quantity: number) {
        const calc = quantity / area * 10
        return Math.round(calc * 100) / 100
    }

    private formatDate(date: string) {
        return moment(date).format('YYYY年MM月DD日')
    }

    @Emit('selectedItems')
    private selectedItems(): ShippingRecordHarvestRecord[] {
        const items:ShippingRecordHarvestRecord[] = this.selectedHarvestRecords.map(record => {
            return {
                id: record.harvestRecordId,
                harvestDate: record.harvestDate,
                measurementTimingId:"",
                planting: {
                    id: record.plantingId,
                    name: record.plantingName
                },
                agriField: {
                    id: record.agriFieldId,
                    name: record.agriFieldName
                },
                qualityData:''
            }
        })
        return items
    }

    @Watch('recordDate')
    onChangeRecordDate() {
        this.search()
    }
}